import * as R from 'ramda';

export type OnboardStep = {
  title: string,
  description: string,
  button?: string,
  article?: string,
  link?: string,
};

export const onboardSteps = {
  review_documents: {
    id: 'review_documents',
    title: 'Hybrid Training Systems Course Prep',
    description: 'Before the start of the course, review sections 1 & 2 in the HTS Manual (do NOT complete the workbook assignments).',
    button: 'View HTS Manual',
  },
  perform_workout: {
    id: 'perform_workout',
    title: 'Perform a Pre-designed Workout in the App',
    description: 'Time for you to learn how you and your clients will perform workouts and enter results. First, download the mobile app on your phone by searching for "gymcloud" in the <b>App Store</b> or <b>Google Play</b>. Download <b>Training App (by GymCloud)</b> and log in using your account information. <br /> <br />Then, you may start a workout using the tutorial button on the <b>Dashboard</b>, or by selecting a workout after navigating to <b>My Training Log</b> from the main menu.',
  },
  got_questions: {
    id: 'got_questions',
    title: 'Visit the Help Center',
    description: 'Visit the Help Center and review the Client articles to understand how your clients will experience the app.',
    button: 'End Tutorial',
    article: 'GymCloud Help Center',
    link: 'https://gymcloud.freshdesk.com/support/solutions/3000001953',
  },
};

export const onboardStepKeys = Object.keys(onboardSteps);

const tourSteps = [
  {
    step: 'thanks_bar',
    content: () => (
      {
        title: 'Thanks for choosing GymCloud!',
        main: `
          At GymCloud, our mission is to enhance the training experience between fitness professionals and clients.
          To accomplish that, we’ve provided you with all the tools you and your clients need to achieve fitness success.
          Let’s take a quick look around.
        `,
      }
    ),
    position: 'center',
  },
  {
    step: 'navigation_bar',
    selector: '[data-tour="tour__nav"]',
    content: () => (
      {
        title: 'Navigation',
        main: `
         This menu is where you’ll navigate through GymCloud and access its core features. The dropdown arrows enable you to quickly find specific items in each category.
         You can easily access your library of <b>Exercises</b>, master <b>Workouts</b> and <b>Programs</b>, and your videos.
        `,
      }
    ),
  },
  {
    step: 'navigation_bar',
    selector: '[data-tour="tour__nav"]',
    content: () => (
      {
        title: 'Navigation',
        main: `
         In the <b>Clients</b> section, you can manage client profiles, groups, and assigned workouts and programs.
         You can quickly navigate to your <b>Calendar</b> for scheduling, or set up the <b>Metrics</b> (sets, reps, etc.) you will use to measure progress.
         Click <b>My Training Log</b> to find the fitness programs you have assigned to yourself.
        `,
      }
    ),
  },
  {
    step: 'profile_avatar',
    selector: '[data-tour="tour__profile"]',
    content: () => (
      {
        title: 'Profile',
        main: 'Click your <b>profile image</b> to edit your profile info, update payment options, or log out.',
      }
    ),
  },
  {
    step: 'help_icon',
    selector: '[data-tour="tour__help"]',
    content: () => (
      {
        title: 'Help Center',
        main: `
          Got questions? We’ve got answers.
          Access our <b>help center</b> using the “?” icon beside your profile image and click on Go to Support Portal.
          Here you can access a library of tutorial articles, or you can quickly get help from Jim, our support bot, by clicking the <b>Support Bot</b> icon in the lower corner of your screen and entering your question.
        `,
      }
    ),
  },
  {
    step: 'next_steps',
    content: () => (
      {
        title: 'Next Steps',
        main: `
          Now let’s begin building your online personal training business.
          The <b>dashboard tutorial</b> will introduce you to the fundamentals of online training.
        `,
      }
    ),
    position: 'center',
  },
];

export const calculateOnboardPercentage = (userSteps: string[]) => {
  const completed = R.intersection(onboardStepKeys, userSteps);
  const percentage = 100 * (completed.length / onboardStepKeys.length);
  return Math.round(percentage);
};

export const getNextStep = (userSteps: string[]) => {
  const completed = R.intersection(onboardStepKeys, userSteps);
  const diff = R.difference(onboardStepKeys, completed);
  if (diff.length === 0) {
    return R.last(onboardStepKeys);
  }
  return diff[0];
};

export const getOnboardSteps = (userSteps: string[]) => {
  const completed = R.intersection(onboardStepKeys, userSteps);
  const diff = R.difference(onboardStepKeys, completed);
  let current = diff[0];
  if (diff.length === 0) {
    current = R.last(onboardStepKeys);
  }

  return {
    current,
    steps: onboardStepKeys.reduce((acc, step) => [
      ...acc,
      {
        name: step,
        isCompleted: completed.indexOf(step) !== -1,
      },
    ], []),
  };
};

export const getTourSteps = (userSteps: string[]) => {
  const completed = R.intersection(
    tourSteps.map(s => s.step),
    userSteps,
  );

  return tourSteps.reduce((acc, item) => [
    ...acc,
    {
      ...item,
      isCompleted: completed.indexOf(item.step) !== -1,
    },
  ], []);
};
